import React from "react";
import { motion } from "framer-motion";
import ContactSection from "../ContactSection/ContactSection";
import {
  TEAM_DATA,
  ABOUTUS_DATA,
  COMPANY_DATA,
} from "../../constant/CompanyData";
import { FaLinkedin, FaTwitter, FaEnvelope, FaBullseye, FaEye, FaGem } from "react-icons/fa";
import Eyebrow from "../common/Eyebrow";

const ABOUTUS_ICONS = [FaBullseye, FaEye, FaGem];

const AboutUs = () => {
  return (
    <div className="bg-white">
      {/* Intro */}
      <section className="bg-white px-6 py-20">
        <div className="mx-auto max-w-4xl text-center">
          <Eyebrow>Who we are</Eyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
            Empowering IT with the strength of rock
          </h2>
          <p className="mx-auto mt-5 max-w-3xl leading-relaxed text-slate-600">
            Established in 2023, QuantaRock Systems is a dynamic and
            innovative company committed to providing comprehensive
            end-to-end solutions for businesses. As a trusted partner in IT
            hardware, procurement, and data center solutions, QuantaRock
            Systems is your go-to source for all things related to the data
            center and IT industry. In today's fast-paced digital landscape,
            a robust IT infrastructure is crucial, and that's where
            QuantaRock Systems excels.
          </p>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>What drives us</Eyebrow>
          </div>
          <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
            Mission, vision, and values
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {ABOUTUS_DATA.map((item, index) => {
              const Icon = ABOUTUS_ICONS[index % ABOUTUS_ICONS.length];
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--website-primary-color)] text-[var(--website-secondary-color)] transition group-hover:bg-[var(--website-secondary-color)] group-hover:text-[var(--website-primary-color)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-[var(--website-primary-color)]">
                    {item.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-slate-600">
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-[var(--surface-dark)] px-6 py-20">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--website-secondary-color)]">
            The people
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">
            Meet our experts
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-300">
            We pride ourselves on delivering excellence and building lasting
            partnerships through tailored solutions.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
            {TEAM_DATA.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col items-center"
              >
                <img
                  src={require(`../../assets/AboutUs/Team/${member.image}`)}
                  alt={member.name}
                  className="h-32 w-32 rounded-full object-cover shadow-lg"
                  loading="lazy"
                />
                <h3 className="mt-4 text-xl font-semibold text-white">
                  {member.name}
                </h3>
                <p className="font-semibold text-slate-300">{member.role}</p>
                <div className="mt-4 flex justify-center gap-4">
                  {member.linkedInURL && (
                    <a
                      href={member.linkedInURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaLinkedin
                        size={24}
                        className="text-white transition hover:text-[var(--website-secondary-color)]"
                      />
                    </a>
                  )}
                  {member.mailURL && (
                    <a href={member.mailURL} target="_blank" rel="noopener noreferrer">
                      <FaEnvelope
                        size={24}
                        className="text-white transition hover:text-[var(--website-secondary-color)]"
                      />
                    </a>
                  )}
                  {member.twitterURL && (
                    <a
                      href={member.twitterURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaTwitter
                        size={24}
                        className="text-white transition hover:text-[var(--website-secondary-color)]"
                      />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Message */}
      <section className="bg-slate-50 px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm md:p-12"
        >
          <Eyebrow>Leadership</Eyebrow>
          <h2 className="mt-3 text-2xl font-bold text-[var(--website-primary-color)] md:text-3xl">
            The visionary behind our success
          </h2>
          <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-slate-600">
            At QuantaRock Systems, our mission is to transform ideas into
            reality and empower businesses with innovative solutions. We take
            pride in our customer-centric approach, continuously seeking
            feedback to refine our offerings and ensure unparalleled
            satisfaction. Whether you are a potential client, a partner, or a
            prospective team member, we are eager to connect with you.
          </p>
          <div className="mt-6">
            <img
              src={require("../../assets/AboutUs/Team/ceo.webp")}
              alt={COMPANY_DATA.ceoName}
              className="mx-auto h-24 w-24 rounded-full object-cover shadow-lg"
              loading="lazy"
            />
            <h3 className="mt-4 text-lg font-semibold text-[var(--website-primary-color)]">
              {COMPANY_DATA.ceoName}
            </h3>
            <p className="font-semibold text-slate-500">
              Chief Executive Officer
            </p>
          </div>
        </motion.div>
      </section>

      <ContactSection />
    </div>
  );
};

export default AboutUs;
